import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Whether this browser can scan with the camera (BarcodeDetector + getUserMedia). */
export const canScan = () =>
  typeof window !== "undefined" &&
  "BarcodeDetector" in window &&
  Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Camera QR scanning with the browser's own BarcodeDetector — no library.
 * Where the browser has none, the caller hides the button and the code is
 * typed instead. The camera is released as soon as a code is read or the
 * dialog closes.
 */
export default function QrScanner({ open, onDetected, onClose }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let stream = null;
    let timer = null;
    let stopped = false;
    setError("");

    const stop = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };

    (async () => {
      try {
        // eslint-disable-next-line no-undef
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (stopped) return stop();
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();

        const tick = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(video);
            const value = codes.find((code) => code.rawValue)?.rawValue;
            if (value) {
              stop();
              onDetected(value.trim());
              return;
            }
          } catch {
            // A frame that could not be read; try the next one.
          }
          timer = setTimeout(tick, 250);
        };
        tick();
      } catch (err) {
        setError(err?.name === "NotAllowedError" ? t("loyalty.scan.denied") : t("loyalty.scan.unavailable"));
      }
    })();

    return stop;
  }, [open, onDetected, t]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("loyalty.scan.title")}
        className="card w-full max-w-sm p-4"
      >
        <h2 className="mb-3 font-semibold">{t("loyalty.scan.title")}</h2>
        {error ? (
          <p
            className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
            role="alert"
          >
            {error}
          </p>
        ) : (
          <video
            ref={videoRef}
            className="aspect-square w-full rounded-lg bg-black object-cover"
            muted
            playsInline
          />
        )}
        <p className="mt-2 text-xs text-ink-2">{t("loyalty.scan.hint")}</p>
        <div className="mt-3 flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
